import React, { useMemo } from 'react';

import type { Employee, Task } from 'types/interfaces';
import { PickerTexts } from 'utils';
import {
  extractRequiredAttainments,
  mapEmployeeAttainments
} from 'utils/taskExecutorPickerHelpers';

import { CustomDropdown } from 'ui/custom-controls';

function skillsColumnBody(row: Employee) {
  const rowEx = row as Employee & {
    availableAttainments?: { id: number; label: string }[];
    unavailableAttainments?: { id: number; label: string }[];
  };
  const { availableAttainments = [], unavailableAttainments = [] } = rowEx;
  const hasSkills =
    availableAttainments.length > 0 || unavailableAttainments.length > 0;
  if (!hasSkills) {
    return (
      <div className="flex items-center justify-center text-gray-500 italic text-sm py-2">
        Плановые работы не выбраны или навыки не требуются
      </div>
    );
  }
  return (
    <div className="flex flex-wrap gap-1">
      {availableAttainments.map((a: any) => (
        <span
          key={a.id}
          style={{
            backgroundColor: '#c6f6d5',
            color: '#22543d',
            padding: '2px 6px',
            borderRadius: '10px',
            fontSize: '12px'
          }}
        >
          {a.label}
        </span>
      ))}
      {unavailableAttainments.map((u: any) => (
        <span
          key={u.id}
          style={{
            backgroundColor: '#fed7d7',
            color: '#742a2a',
            padding: '2px 6px',
            borderRadius: '10px',
            fontSize: '12px'
          }}
        >
          {u.label}
        </span>
      ))}
    </div>
  );
}

const EXECUTOR_DROPDOWN_COLUMNS: {
  field: keyof Employee;
  header: string;
  width?: string;
  body?: (row: Employee) => React.ReactNode;
}[] = [
  { field: 'personnelNumber', header: 'Табельный номер', width: '22%' },
  { field: 'fullName', header: 'ФИО', width: '30%' },
  {
    field: 'id',
    header: 'Навыки для плановых работ',
    width: '45%',
    body: skillsColumnBody
  }
];

export interface TaskExecutorPickerProps {
  task: Task;
  rawExecutors: Employee[];
  selectedExecutors: Employee[];
  onSelectionChange: (employees: Employee[]) => void;
  showAllEmployees: boolean;
  onToggleShowAll: () => void;
  pickerKey: string;
  emptyMessage: string;
  minWidth?: number;
  overlayWidthMultiplier?: number;
  showSkillsColumn?: boolean;
}

export function TaskExecutorPicker({
  task,
  rawExecutors,
  selectedExecutors,
  onSelectionChange,
  showAllEmployees,
  onToggleShowAll,
  pickerKey,
  emptyMessage,
  minWidth = 280,
  overlayWidthMultiplier = 1.25,
  showSkillsColumn = true
}: TaskExecutorPickerProps) {
  const columns = useMemo(() => {
    if (!showSkillsColumn) {
      return [
        { field: 'personnelNumber' as keyof Employee, header: 'Табельный номер', width: '38%' },
        { field: 'fullName', header: 'ФИО', width: '62%' }
      ] satisfies {
        field: keyof Employee;
        header: string;
        width?: string;
        body?: (row: Employee) => React.ReactNode;
      }[];
    }
    return EXECUTOR_DROPDOWN_COLUMNS;
  }, [showSkillsColumn]);

  const executorsOptions = useMemo(() => {
    if (!showSkillsColumn) {
      return rawExecutors;
    }
    const required = extractRequiredAttainments(task);
    return mapEmployeeAttainments(rawExecutors, required, {
      start: task.plannedStartDate,
      end: task.plannedEndDate
    });
  }, [task, rawExecutors, showSkillsColumn]);

  return (
    <div style={{ minWidth }}>
      <CustomDropdown<Employee>
        key={pickerKey}
        multiplySelectMode
        assetOptions={executorsOptions}
        selectedAsset={selectedExecutors}
        onSelectionChange={(e) =>
          onSelectionChange((Array.isArray(e) ? e : []) as Employee[])
        }
        placeholder={PickerTexts.executors(selectedExecutors?.length || 0)}
        showToggleButton
        onToggleShowAll={onToggleShowAll}
        showAll={showAllEmployees}
        toggleShowAllTooltip="Показать всех сотрудников"
        toggleShowFilteredTooltip="Показать только подходящих сотрудников"
        columns={columns}
        emptyMessage={emptyMessage}
        showClearOnHover
        overlayWidthMultiplier={overlayWidthMultiplier}
        filterWithLabel
      />
    </div>
  );
}
